import React from 'react'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Modal as MuiModal, Backdrop, Paper } from '@mui/material'
import ModalScrollContent from './ModalScrollContent'
import { useSpring, animated } from 'react-spring/web' // web.cjs is required for IE 11 support

const useStyles = makeStyles()((theme: Theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxHeight: '100vh',
    padding: theme.spacing(6),
  },
  content: {
    [theme.breakpoints.up('sm')]: {
      minWidth: 320,
      maxWidth: `calc(100vw - ${theme.spacing(12)})`,
    },
    [theme.breakpoints.down('md')]: {
      maxWidth: `calc(100vw - ${theme.spacing(1)})`,
    },
    margin: 'auto',
    width: '100%',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
  },
  large: {
    width: 672,
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
    },
  },
  header: {
    borderTopRightRadius: theme.spacing(0.5),
    borderTopLeftRadius: theme.spacing(0.5),
  },
  footer: {
    borderBottomRightRadius: theme.spacing(0.5),
    borderBottomLeftRadius: theme.spacing(0.5),
    width: '100%',
    borderTop: `1px solid ${theme.palette.divider}`,
    minHeight: theme.spacing(8.5),
    backgroundColor: theme.palette.background.paper,
    display: 'flex',
    alignItems: 'center',
  },
}))

type FadeProps = {
  children?: React.ReactElement
  in: boolean
  onEnter?: () => {}
  onExited?: () => {}
}

const Fade = React.forwardRef<HTMLDivElement, FadeProps>(function Fade(
  props,
  ref
) {
  const { in: open, children, onEnter, onExited, ...other } = props
  const style = useSpring({
    from: { opacity: 0 },
    to: { opacity: open ? 1 : 0 },
    onStart: () => {
      if (open && onEnter) {
        onEnter()
      }
    },
    onRest: () => {
      if (!open && onExited) {
        onExited()
      }
    },
  })

  return (
    <animated.div ref={ref} style={style} {...other}>
      {children}
    </animated.div>
  )
})

export type ModalProps = {
  /**
   * 開閉状態
   */
  open: boolean
  /**
   * 閉じる時に発火する関数
   */
  handleClose: () => void
  /**
   * trueの時はコンテンツ幅が672px
   */
  large?: boolean
  /**
   * モーダル内に表示されるコンテンツ
   */
  children?: any
  /**
   * 固定ヘッダコンテンツ
   */
  header?: any
  /**
   * 固定フッタコンテンツ
   */
  footer?: any
}

const Modal: React.VFC<ModalProps> = ({
  open,
  handleClose,
  children,
  large,
  header,
  footer,
}: ModalProps) => {
  const { classes, cx } = useStyles()

  return (
    <MuiModal
      disableEnforceFocus
      disableAutoFocus
      aria-labelledby="spring-modal-title"
      aria-describedby="spring-modal-description"
      className={classes.modal}
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
          style: {
            zIndex: -1,
          },
        },
      }}
    >
      <Fade in={open}>
        <Paper
          elevation={0}
          className={cx(classes.content, large && classes.large)}
        >
          <ModalScrollContent
            header={header && <div className={classes.header}>{header}</div>}
            footer={footer && <div className={classes.footer}>{footer}</div>}
          >
            {children}
          </ModalScrollContent>
        </Paper>
      </Fade>
    </MuiModal>
  )
}

Modal.defaultProps = {
  large: false,
}

export default Modal
